import React, { useState, useRef } from "react";
import { MapPin, Phone, Mail, Send, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import emailjs from "@emailjs/browser"; // 1. Import EmailJS

const ContactSection = () => {
  const form = useRef(); // 2. Create a reference to the form
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "error"

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus("loading");

    // 3. Replace these values with yours from the EmailJS Dashboard
    const SERVICE_ID = "YOUR_SERVICE_ID";
    const TEMPLATE_ID = "YOUR_TEMPLATE_ID";
    const PUBLIC_KEY = "YOUR_PUBLIC_KEY";

    emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, form.current, PUBLIC_KEY)
      .then((result) => {
          console.log(result.text);
          setStatus("success");
          e.target.reset(); // Clear form on success
      }, (error) => {
          console.log(error.text);
          setStatus("error");
      });
  };

  return (
    <section className="relative w-full py-20 px-4 md:px-8 bg-gray-100 flex items-center justify-center min-h-[800px]" id="contact">
      
      {/* Background Map Image */}
      <div className="absolute inset-0 w-full h-full z-0">
        <img 
          src="https://images.unsplash.com/photo-1524661135-423995f22d0b?q=80&w=2074&auto=format&fit=crop"
          alt="Map Background"
          className="w-full h-full object-cover grayscale opacity-40"
        />
        <div className="absolute inset-0 bg-gray-900/10 backdrop-blur-[2px]"></div>
      </div>

      <div className="relative z-10 w-full max-w-[1600px] bg-white rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col lg:flex-row">
        
        {/* LEFT: Contact Information */}
        <div className="w-full lg:w-5/12 bg-[#0f172a] p-10 md:p-14 text-white flex flex-col justify-between">
          <div>
            <h3 className="text-3xl font-bold mb-6">Kontaktieren Sie uns</h3>
            <p className="text-gray-400 mb-10 leading-relaxed">
              Haben Sie Fragen zu unseren Leistungen oder benötigen Sie ein individuelles Angebot? 
              Unser Team steht Ihnen gerne zur Verfügung.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-[#ff4a4a]">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-gray-400 uppercase tracking-wider font-semibold">Telefon</p>
                  <p className="text-xl font-medium mt-1">+41 78 709 67 02</p>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-[#ff4a4a]">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-gray-400 uppercase tracking-wider font-semibold">E-Mail</p>
                  <a href="mailto:info@sani-style.ch" className="text-xl font-medium mt-1 hover:text-[#ff4a4a] transition-colors">
                    info@sani-style.ch
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-[#ff4a4a]">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-gray-400 uppercase tracking-wider font-semibold">Standort</p>
                  <p className="text-xl font-medium mt-1">
                    Solothurnstrasse 46 3294 Büren an der Aare
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10">
             <p className="text-gray-400 text-sm">
               Wir antworten in der Regel innerhalb von <span className="text-white font-bold">24 Stunden</span> an Werktagen.
             </p>
          </div>
        </div>

        {/* RIGHT: Contact Form */}
        <div className="w-full lg:w-7/12 bg-white p-10 md:p-14">
          <div className="mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-2">Nachricht senden</h3>
            <p className="text-gray-500">Füllen Sie das Formular aus und wir melden uns umgehend bei Ihnen.</p>
          </div>

          {/* 4. Add the 'ref' to your form */}
          <form ref={form} onSubmit={handleSubmit} className="space-y-6">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Vor- und Nachname</label>
                <input 
                  type="text" 
                  name="user_name" // 5. Ensure these match your EmailJS template placeholders {{user_name}}
                  required
                  placeholder="Max Mustermann"
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-[#ff4a4a] focus:ring-1 focus:ring-[#ff4a4a] outline-none transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">E-Mail Adresse</label>
                <input 
                  type="email" 
                  name="user_email" // 5. Template placeholder: {{user_email}}
                  required
                  placeholder="max@beispiel.ch"
                  className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-[#ff4a4a] focus:ring-1 focus:ring-[#ff4a4a] outline-none transition-all"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Worum geht es?</label>
              <select 
                name="service_type" // 5. Template placeholder: {{service_type}}
                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-[#ff4a4a] focus:ring-1 focus:ring-[#ff4a4a] outline-none transition-all text-gray-600"
              >
                <option>Sanitär (Installation/Reparatur)</option>
                <option>Heizung & Wärme</option>
                <option>Wartung & Service</option>
                <option>Badumbau / Sanierung</option>
                <option>Allgemeine Anfrage</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">Ihre Nachricht</label>
              <textarea 
                name="message" // 5. Template placeholder: {{message}}
                required
                rows="4" 
                placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-[#ff4a4a] focus:ring-1 focus:ring-[#ff4a4a] outline-none transition-all resize-none"
              ></textarea>
            </div>

            {status === "success" && (
              <div className="p-4 bg-green-50 text-green-700 rounded-xl flex items-center gap-2">
                <CheckCircle className="w-5 h-5" /> 
                <span>Nachricht erfolgreich gesendet! Wir melden uns bei Ihnen.</span>
              </div>
            )}
            
            {status === "error" && (
              <div className="p-4 bg-red-50 text-red-700 rounded-xl flex items-center gap-2">
                <AlertCircle className="w-5 h-5" /> Etwas ist schief gelaufen. Bitte versuchen Sie es später erneut.
              </div>
            )}

            <button 
              type="submit"
              disabled={status === "loading"}
              className="w-full py-4 rounded-xl font-bold text-white shadow-lg shadow-red-500/30 hover:shadow-red-500/50 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 disabled:hover:translate-y-0"
              style={{ backgroundColor: "#ff4a4a" }}
            >
              {status === "loading" ? (
                <>Senden... <Loader2 className="w-5 h-5 animate-spin" /></>
              ) : (
                <>Nachricht senden <Send className="w-5 h-5" /></>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;